import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { PMDialog } from "../../utils/PMDialog";
import { QuillEditor } from "../../utils/quill-editor";
import { FormikSendRecipientPicker, SendDialogActions } from "../shared/SendRecipientPicker";
import { purchasesApi } from "../../../api/inventory/purchases";
import { fetchContact } from "../../../api/customer/contact";

function contactName(row) {
  return row?.name || row?.displayTitle || "";
}

function recipientFrom(row) {
  const email = row?.email?.trim();
  if (!email) return null;
  return { email, name: contactName(row) };
}

export const PurchaseSendDialog = ({ onClose, open, purchase, onSuccess }) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [contact, setContact] = useState(purchase?.contact || null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);
  const supplierId = purchase?.supplier_id || purchase?.supplier?.id;
  const contactId = purchase?.contact_id || purchase?.contact?.id;

  useEffect(() => {
    let active = true;
    if (!open) {
      return () => { active = false; };
    }
    if (purchase?.contact?.email) {
      setContact(purchase.contact);
      setIsLoadingContact(false);
      return () => { active = false; };
    }
    if (!contactId) {
      setContact(null);
      setIsLoadingContact(false);
      return () => { active = false; };
    }
    setIsLoadingContact(true);
    fetchContact(contactId)
      .then((response) => { if (active) setContact(response?.data?.data || response?.data || null); })
      .catch(() => { if (active) toast.error(t("inventoryPurchase.contactEmailMissing")); })
      .finally(() => { if (active) setIsLoadingContact(false); });
    return () => { active = false; };
  }, [open, contactId, purchase?.contact, t]);

  const contactRecipient = recipientFrom(contact);
  const supplierRecipient = recipientFrom(purchase?.supplier);
  const defaultRecipient = contactRecipient || supplierRecipient || { email: "", name: "" };
  const recipientOptions = [
    currentUser?.email && { key: "me", label: t("inventoryPurchase.sendAsMe"), email: currentUser.email, name: currentUser.name },
    supplierRecipient && { key: "supplier", label: t("inventoryPurchases.supplier"), email: supplierRecipient.email, name: supplierRecipient.name },
    {
      key: "contact",
      label: isLoadingContact ? t("inventoryPurchase.loading") : (contactName(contact) ? t("inventoryPurchase.sendToContactNamed", { name: contactName(contact) }) : t("inventoryPurchase.sendToContact")),
      email: contactRecipient?.email,
      name: contactRecipient?.name,
      disabled: isLoadingContact || !contactRecipient,
    },
  ].filter(Boolean);

  return (
    <PMDialog open={open} onClose={onClose} title={t("inventoryPurchase.sendDialogTitle")} size="2xl" closeOnOutsideClick>
      <p className="mb-5 text-sm text-slate-600">{t("inventoryPurchase.sendQueueHint")}</p>
      {!purchase?.id ? (
        <p className="text-sm text-red-600">{t("inventoryPurchase.missingId")}</p>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            email: defaultRecipient.email,
            name: defaultRecipient.name,
            message: "",
          }}
          validationSchema={Yup.object({
            email: Yup.string().trim().email(t("documentationSend.invalid_email")).required(t("documentationSend.email_required")),
          })}
          onSubmit={async (values, helpers) => {
            try {
              const response = await purchasesApi.sendToSupplier(purchase.id, {
                email: values.email.trim(),
                name: values.name,
                message: values.message,
              });
              const deliveryState = response?.data?.data?.delivery_state;
              if (!response?.data?.success || !["queued", "accepted"].includes(deliveryState)) {
                throw new Error(response?.data?.errors?.join(", ") || t("inventoryPurchase.sendFailed"));
              }
              toast.success(t(deliveryState === "accepted" ? "inventoryPurchase.delivery.accepted" : "inventoryPurchase.deliveryQueued"));
              helpers.setStatus({ success: true });
              onSuccess?.();
              onClose?.();
            } catch (error) {
              // Inline submit error owns presentation (no parallel toast).
              const serverMessage = error?.response?.data?.errors?.filter(Boolean).join(", ")
                || error?.response?.data?.problem?.message;
              const message = serverMessage || error?.message || t("inventoryPurchase.sendFailed");
              helpers.setStatus({ success: false });
              helpers.setErrors({ submit: message });
            } finally {
              helpers.setSubmitting(false);
            }
          }}
        >
          {(formik) => (
            <Form className="space-y-5">
              <FormikSendRecipientPicker formik={formik} options={recipientOptions} customerId={supplierId} autoFocus />
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">{t("inventoryPurchase.sendMessageLabel")}</label>
                <QuillEditor name="message" rows={4} {...formik} />
              </div>
              {formik.errors.submit && <p role="alert" className="text-sm text-red-600">{formik.errors.submit}</p>}
              <SendDialogActions
                onCancel={onClose}
                onSubmit={formik.submitForm}
                submitting={formik.isSubmitting}
                cancelLabel={t("inventoryPurchase.close")}
                sendLabel={t("inventoryPurchase.send")}
                sendingLabel={t("inventoryPurchase.sending")}
              />
            </Form>
          )}
        </Formik>
      )}
    </PMDialog>
  );
};
